/**
 * SiteContentEditor — Stage 2 Master Admin
 * Sub-tabs:
 *  1. Pages — edit per-page overrides (hero copy, CTAs) with WYSIWYG support
 *  2. Property Types — reorder / rename / enable the homepage Property Types row
 *  3. Pinned Hotels — choose which hotels appear at the top of search/featured
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Pin, PinOff, ArrowUp, ArrowDown, RefreshCw, FileText, LayoutGrid, Star, Search } from 'lucide-react';
import Editor from 'react-simple-wysiwyg';
import { Button } from '../ui/button';
import { invalidateSiteContent } from '../../hooks/useSiteContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Per-page editable schema -----------------------------------------
// Each entry: { key, label, type ('text'|'html'|'number'), defaultValue, hint }
const PAGE_SCHEMA = {
  homepage: {
    title: 'Homepage',
    fields: [
      { key: 'hero_title', label: 'Hero — Punchline', type: 'text', defaultValue: 'Real prices. Real properties. Real cashbacks' },
      { key: 'hero_tagline_html', label: 'Hero — Tagline (rich text)', type: 'html', defaultValue: 'Earn cashback when you book, review & refer. Redeem it on your next trip.' },
      { key: 'search_placeholder', label: 'Search — Placeholder', type: 'text', defaultValue: 'You are staying to?' },
      { key: 'section_top_hotels', label: 'Section title — Top Hotels', type: 'text', defaultValue: 'Top Hotels in India' },
      { key: 'section_why_book', label: 'Section title — Why book with us', type: 'text', defaultValue: 'Why book with localliink.com' },
    ],
  },
  insights: {
    title: 'Insights page',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'India accommodation, in numbers' },
      { key: 'hero_subtitle_html', label: 'Hero subtitle', type: 'html', defaultValue: "Real data on India's hospitality market — sourced from Ministry of Tourism, FHRAI and IBEF." },
      { key: 'cta_title', label: 'CTA — title', type: 'text', defaultValue: 'Got a Punjab travel story?' },
      { key: 'cta_subtitle', label: 'CTA — subtitle', type: 'text', defaultValue: 'We feature reader-submitted city guides & hotel reviews every month. Pitch us your story.' },
      { key: 'cta_button', label: 'CTA — button label', type: 'text', defaultValue: 'Submit your story →' },
    ],
  },
  reviews: {
    title: 'Reviews page',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'What travellers are saying' },
      { key: 'hero_subtitle', label: 'Hero subtitle', type: 'text', defaultValue: 'Real reviews from real stays across Punjab, Haryana & Chandigarh.' },
      { key: 'cta_title', label: 'CTA — title', type: 'text', defaultValue: 'Earn ₹20 for an honest review' },
      { key: 'cta_subtitle', label: 'CTA — subtitle', type: 'text', defaultValue: 'Rate above 6 / 10 on a completed booking and get ₹10 instantly. Add a thoughtful comment to earn ₹10 more.' },
    ],
  },
  'staff-training-programme': {
    title: 'Staff Training Programme',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'Staff Training Programme' },
      { key: 'hero_subtitle_html', label: 'Hero subtitle', type: 'html', defaultValue: 'Upskill your team with hospitality-grade workshops.' },
      { key: 'cta_title', label: 'CTA — title', type: 'text', defaultValue: 'Ready to upskill your team?' },
    ],
  },
  'skill-development-programme': {
    title: 'Skill Development Programme',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'Skill Development Programme' },
      { key: 'hero_subtitle_html', label: 'Hero subtitle', type: 'html', defaultValue: 'Govt-aligned skill certification for hotel & hospitality staff.' },
      { key: 'cta_title', label: 'CTA — title', type: 'text', defaultValue: 'Enrol your team today' },
    ],
  },
  'liink-certification': {
    title: 'LCP — Liink Certification Programme',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'LCP — Liink Certification Programme' },
      { key: 'hero_subtitle_html', label: 'Hero subtitle', type: 'html', defaultValue: 'Independent hotel certification by localliink experts.' },
      { key: 'cta_title', label: 'CTA — title', type: 'text', defaultValue: 'Apply for LCP audit' },
    ],
  },
  'list-your-property': {
    title: 'List Your Property',
    fields: [
      { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: 'List your property on localliink.com' },
      { key: 'hero_subtitle_html', label: 'Hero subtitle', type: 'html', defaultValue: 'Reach millions of Indian travellers. Zero setup fee. Pay only on bookings.' },
      { key: 'cta_button', label: 'Final CTA button', type: 'text', defaultValue: 'Get started →' },
    ],
  },
  // ===== Iter 28: Static pages (StaticPage component) =====
  // Each new page accepts hero_title, hero_subtitle_html and body_html.
  ...Object.fromEntries(
    [
      ['about', 'About localliink.com'],
      ['press', 'Press Centre'],
      ['careers', 'Careers (with resume form)'],
      ['investor-relations', 'Investor Relations'],
      ['trust-safety', 'Trust & Safety'],
      ['cancellation-policy', 'Cancellation Policy'],
      ['refund-status', 'Refund Status'],
      ['travel-guides', 'Travel Guides & City Stories'],
      ['loved-by-travellers', 'Loved by Travellers'],
      ['connect', 'Connect'],
      ['customer-support', 'Customer Support (with email form)'],
      ['explore', 'Explore section'],
      ['spend-wallet-credits', 'Spend Wallet Credits'],
    ].map(([id, name]) => [id, {
      title: name,
      fields: [
        { key: 'hero_title', label: 'Hero title', type: 'text', defaultValue: name },
        { key: 'hero_subtitle_html', label: 'Hero subtitle (rich text)', type: 'html', defaultValue: '' },
        { key: 'body_html', label: 'Body content (rich text)', type: 'html', defaultValue: '' },
      ],
    }])
  ),
};

export const SiteContentEditor = () => {
  const [tab, setTab] = useState('pages');
  return (
    <div data-testid="site-content-editor">
      <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="content-subtabs">
        {[
          { id: 'pages', label: 'Pages & Hero Copy', icon: <FileText className="h-4 w-4" /> },
          { id: 'property-types', label: 'Property Types', icon: <LayoutGrid className="h-4 w-4" /> },
          { id: 'pinned', label: 'Pinned Hotels', icon: <Star className="h-4 w-4" /> },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`content-subtab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-[#FF0059] text-[#FF0059]' : 'border-transparent text-slate-500 hover:text-black'
            }`}
          >{t.icon} {t.label}</button>
        ))}
      </div>
      {tab === 'pages' && <PagesEditor />}
      {tab === 'property-types' && <PropertyTypesEditor />}
      {tab === 'pinned' && <PinnedHotelsEditor />}
    </div>
  );
};

// =============================================================================
// 1. PAGES EDITOR
// =============================================================================
const PagesEditor = () => {
  const pageIds = Object.keys(PAGE_SCHEMA);
  const [pageId, setPageId] = useState(pageIds[0]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (pid) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/site-content/${pid}`, { withCredentials: true });
      setValues(data?.sections || {});
    } catch {
      setValues({});
      toast.error('Failed to load page content');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(pageId); }, [pageId, load]);

  const setField = (key, v) => setValues((p) => ({ ...p, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty strings to fall back to hardcoded defaults
      const clean = {};
      Object.entries(values).forEach(([k, v]) => {
        if (typeof v === 'string') {
          if (v.trim()) clean[k] = v;
        } else if (v !== undefined && v !== null) {
          clean[k] = v;
        }
      });
      await axios.put(`${API}/admin/site-content/${pageId}`, { sections: clean }, { withCredentials: true });
      invalidateSiteContent(pageId);
      toast.success('Saved — live on preview now');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Remove all admin overrides for this page? Hardcoded defaults will be used.')) return;
    try {
      await axios.delete(`${API}/admin/site-content/${pageId}`, { withCredentials: true });
      setValues({});
      invalidateSiteContent(pageId);
      toast.success('Reset to defaults');
    } catch { toast.error('Reset failed'); }
  };

  const schema = PAGE_SCHEMA[pageId];

  return (
    <div data-testid="pages-editor" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Page:</label>
        <select
          value={pageId}
          onChange={(e) => setPageId(e.target.value)}
          className="px-3 py-2 rounded-full border border-slate-200 focus:border-[#FF0059] outline-none text-sm font-bold"
          data-testid="page-selector"
        >
          {pageIds.map((id) => (
            <option key={id} value={id}>{PAGE_SCHEMA[id].title}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={reset} className="rounded-full font-bold" data-testid="page-reset-btn">
          <RefreshCw className="h-3 w-3 mr-1" /> Reset
        </Button>
      </div>
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="space-y-4 bg-white border border-slate-200 rounded-2xl p-5" data-testid="page-fields">
          {schema.fields.map((f) => (
            <FieldRow key={f.key} field={f} value={values[f.key]} onChange={(v) => setField(f.key, v)} />
          ))}
        </div>
      )}
      <Button onClick={save} disabled={saving || loading} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="page-save-btn">
        <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
};

const FieldRow = ({ field, value, onChange }) => {
  const cur = value ?? '';
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 block mb-1.5">{field.label}</label>
      {field.type === 'html' ? (
        <div className="rounded-xl overflow-hidden border border-slate-200 focus-within:border-[#FF0059]" data-testid={`field-${field.key}`}>
          <Editor
            value={cur || ''}
            onChange={(e) => onChange(e.target.value)}
            containerProps={{ style: { minHeight: 120 } }}
          />
        </div>
      ) : (
        <input
          type="text"
          value={cur}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.defaultValue}
          className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-[#FF0059] outline-none text-sm"
          data-testid={`field-${field.key}`}
        />
      )}
      <p className="text-[10px] text-slate-400 mt-1 italic">Default: {field.defaultValue.substring(0, 120)}{field.defaultValue.length > 120 ? '…' : ''}</p>
    </div>
  );
};

// =============================================================================
// 2. PROPERTY TYPES EDITOR  — stored under page_id="property-types", key "items"
// =============================================================================
const DEFAULT_PROPERTY_TYPES = [
  { id: 'hotel', label: 'Hotels', icon: 'Hotel', count: 28, enabled: true },
  { id: 'motel', label: 'Motels', icon: 'BedDouble', count: 4, enabled: true },
  { id: 'resort', label: 'Resorts', icon: 'Palmtree', count: 3, enabled: true },
  { id: 'homestay', label: 'Homestays', icon: 'Home', count: 12, enabled: true },
  { id: 'apartment', label: 'Apartments', icon: 'Building2', count: 9, enabled: true },
  { id: 'farmhouse', label: 'Farm Houses', icon: 'Trees', count: 6, enabled: true },
  { id: 'bunkbed', label: 'Bunk Beds', icon: 'Bed', count: 5, enabled: true },
  { id: 'hostel', label: 'Hostel & PG', icon: 'Users', count: 11, enabled: true },
  { id: 'guesthouse', label: 'Guest Houses', icon: 'Tent', count: 8, enabled: true },
];

const PropertyTypesEditor = () => {
  const [items, setItems] = useState(DEFAULT_PROPERTY_TYPES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/site-content/property-types`, { withCredentials: true });
        const overrideItems = data?.sections?.items;
        if (Array.isArray(overrideItems) && overrideItems.length > 0) setItems(overrideItems);
      } catch (err) {
        console.error('[SiteContentEditor] property-types fetch failed:', err);
        /* keep defaults */
      } finally { setLoading(false); }
    })();
  }, []);

  const updateItem = (idx, patch) => {
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const move = (idx, dir) => {
    setItems((p) => {
      const next = [...p];
      const ni = idx + dir;
      if (ni < 0 || ni >= next.length) return p;
      [next[idx], next[ni]] = [next[ni], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content/property-types`,
        { sections: { items } }, { withCredentials: true });
      invalidateSiteContent('property-types');
      toast.success('Property types saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset Property Types to defaults?')) return;
    try {
      await axios.delete(`${API}/admin/site-content/property-types`, { withCredentials: true });
      setItems(DEFAULT_PROPERTY_TYPES);
      invalidateSiteContent('property-types');
      toast.success('Reset to defaults');
    } catch { toast.error('Reset failed'); }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div data-testid="property-types-editor" className="space-y-3">
      <p className="text-xs text-slate-500">Reorder, rename or hide property types shown on the Homepage. Saved changes go live immediately.</p>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
        {items.map((it, idx) => (
          <div key={it.id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`pt-row-${it.id}`}>
            <span className="w-7 text-center text-xs font-black text-slate-400">{idx + 1}</span>
            <input
              value={it.label}
              onChange={(e) => updateItem(idx, { label: e.target.value.slice(0, 40) })}
              className="flex-1 min-w-[160px] px-3 py-1.5 rounded-lg border border-slate-200 focus:border-[#FF0059] outline-none text-sm font-bold"
              data-testid={`pt-label-${it.id}`}
            />
            <input
              type="number"
              value={Number(it.count) || 0}
              onChange={(e) => updateItem(idx, { count: Number(e.target.value) || 0 })}
              className="w-20 px-2 py-1.5 rounded-lg border border-slate-200 focus:border-[#FF0059] outline-none text-sm text-right"
              data-testid={`pt-count-${it.id}`}
              title="Hotel count badge"
            />
            <label className="flex items-center gap-1 text-xs font-bold cursor-pointer" data-testid={`pt-enabled-${it.id}`}>
              <input
                type="checkbox"
                checked={it.enabled !== false}
                onChange={(e) => updateItem(idx, { enabled: e.target.checked })}
              /> visible
            </label>
            <div className="flex gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30" data-testid={`pt-up-${it.id}`}><ArrowUp className="h-3 w-3" /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30" data-testid={`pt-down-${it.id}`}><ArrowDown className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold rounded-full" data-testid="pt-save-btn">
          <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save property types'}
        </Button>
        <Button onClick={reset} variant="outline" className="font-bold rounded-full" data-testid="pt-reset-btn">
          <RefreshCw className="h-3 w-3 mr-1" /> Reset
        </Button>
      </div>
    </div>
  );
};

// =============================================================================
// 3. PINNED HOTELS EDITOR
// =============================================================================
const PinnedHotelsEditor = () => {
  const [hotels, setHotels] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/hotels`, { withCredentials: true });
      // Sort: pinned first (by pin_order asc), then unpinned
      data.sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        if (a.is_pinned && b.is_pinned) return (a.pin_order || 0) - (b.pin_order || 0);
        return (a.hotel_name || '').localeCompare(b.hotel_name || '');
      });
      setHotels(data);
    } catch { toast.error('Failed to load hotels'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePin = async (h, pinned, order = 0) => {
    try {
      await axios.put(`${API}/admin/hotels/${h._id}/pin`,
        { pinned, order }, { withCredentials: true });
      toast.success(pinned ? 'Pinned to top' : 'Unpinned');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Pin failed'); }
  };

  const pinned = hotels.filter((h) => h.is_pinned);
  const filtered = hotels.filter((h) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (h.hotel_name || '').toLowerCase().includes(s)
      || (h.city_area || '').toLowerCase().includes(s);
  });

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div data-testid="pinned-hotels-editor" className="space-y-3">
      <p className="text-xs text-slate-500">
        Pin hotels to surface them at the top of search & featured grids. Currently pinned:{' '}
        <span className="font-bold text-[#FF0059]" data-testid="pinned-count">{pinned.length}</span>
      </p>
      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-slate-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search hotels…"
          className="flex-1 px-3 py-2 rounded-full border border-slate-200 focus:border-[#FF0059] outline-none text-sm"
          data-testid="pinned-search"
        />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
        {filtered.map((h) => (
          <div key={h._id} className="p-3 flex items-center gap-3" data-testid={`pin-row-${h._id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-black truncate">{h.hotel_name}</p>
              <p className="text-xs text-slate-500 truncate">{h.city_area || '—'}</p>
            </div>
            {h.is_pinned ? (
              <>
                <input
                  type="number"
                  value={h.pin_order || 0}
                  onChange={(e) => {
                    const ord = Number(e.target.value) || 0;
                    setHotels((p) => p.map((x) => x._id === h._id ? { ...x, pin_order: ord } : x));
                  }}
                  onBlur={(e) => togglePin(h, true, Number(e.target.value) || 0)}
                  className="w-16 px-2 py-1 rounded-lg border border-slate-200 focus:border-[#FF0059] outline-none text-xs font-bold text-center"
                  data-testid={`pin-order-${h._id}`}
                  title="Pin order (0 = top)"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => togglePin(h, false)}
                  className="rounded-full font-bold text-xs border-[#FF0059] text-[#FF0059]"
                  data-testid={`unpin-${h._id}`}
                >
                  <PinOff className="h-3 w-3 mr-1" /> Unpin
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                onClick={() => togglePin(h, true, pinned.length)}
                className="rounded-full font-bold text-xs bg-[#FF0059] hover:bg-[#CC0047] text-white"
                data-testid={`pin-${h._id}`}
              >
                <Pin className="h-3 w-3 mr-1" /> Pin
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
